import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router, RouterLinkActive, RouterLink, RouterOutlet } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { PopupDialog } from '@app/Common/popup/popup.dialog';
import { ToastMessageType, ToastService } from '@app/Common/toast/';
import { ModuleUniqueIds } from '@app/Constants/ModuleUniqueIds';
import { FormFlowService } from '@app/Services/formFlow.service';
import { LinkedFieldsService } from '@app/Services/linkedFields.service';
import { PermissionService } from '@app/Services/permission.service';
import { TenantService } from '@app/Services/tenant.service';
import { Field, FieldGroup, Step } from '../Models';
import { Patch } from '../Models/Patch';
import { ModuleService } from '../Services/module.service';
import { PackageService } from '../Services/package.service';
import { TranslationService } from '../Services/translation.service';
import { ModuleData } from './module.resolve-guard';
import { FieldService } from './step/fields/field.service';
import { MatSidenavContainer, MatSidenav } from '@angular/material/sidenav';
import { EditFieldComponent } from './step/edit-field/edit-field.component';
import { findRuleReferences } from './step/fields/field-rule-references';
import { MatTabNav, MatTabLink, MatTabNavPanel } from '@angular/material/tabs';
import { ListEditorComponent } from '@app/Common/list.editor';

@Component({
    selector: 'my-modules',
    templateUrl: './module.component.html',
    styleUrls: ['./module.component.css'],
    imports: [MatSidenavContainer, MatSidenav, EditFieldComponent, MatTabNav, RouterLinkActive, MatTabLink, RouterLink, MatTabNavPanel, RouterOutlet]
})
export class ModuleComponent implements OnInit, OnDestroy {
    @ViewChild('rightside', { static: true }) rightside: any;
    blockUi = false;
    headerText = 'Modules';
    subHeaderText = '';
    subSubHeaderText = '';
    moduleVersion = '';
    moduleId = '';
    version = 1;
    stepId = '';
    menuToggled = false;
    listAliases: any[];
    fieldStateCache: any = {};

    private editField$: Observable<{ fieldGroup: FieldGroup; field: Field }>;
    private deleteField$: Observable<Field>;
    private editList$: Observable<{ listAlias: string; fieldName: string }>;
    private addList$: Observable<any>;

    private subscriptions: Subscription[] = [];

    constructor(
        private activatedRoute: ActivatedRoute,
        private moduleService: ModuleService,
        private tenantService: TenantService,
        private packageService: PackageService,
        private linkedFieldsService: LinkedFieldsService,
        private router: Router,
        private ref: ChangeDetectorRef,
        public permissionService: PermissionService,
        public toast: ToastService,
        private translationService: TranslationService,
        private formFlowService: FormFlowService,
        public dialog: MatDialog,
        public fieldService: FieldService
    ) { }

    ngOnInit(): void {
        this.subscriptions.push(this.activatedRoute.queryParams.subscribe(queryParams => this.stepId = queryParams.stepId));
        this.subscriptions.push(this.activatedRoute.data.subscribe(data => {
            const moduleData: ModuleData = data.moduleData;
            this.version = moduleData.version;
            this.moduleId = moduleData.moduleId;
            this.subHeaderText = this.moduleService.moduleContent.ModuleName;
            this.subSubHeaderText = '';
            this.moduleVersion = `version ${this.version}`;
        }));

        this.tenantService.getLists()
            .subscribe(r => {
                this.listAliases = r;
            });

        this.editField$ = this.fieldService.getEditCustomField();
        this.subscriptions.push(this.editField$.subscribe(fieldGroupAndField => {
            if (fieldGroupAndField === null) {
                return;
            }
            this.rightside.toggle();
        }));

        this.deleteField$ = this.fieldService.getDeleteCustomField();
        this.subscriptions.push(this.deleteField$.subscribe(field => {
            if (field !== null) {
                this.deleteCustomField(field);
            }
        }));

        this.editList$ = this.moduleService.getEditList();
        this.subscriptions.push(this.editList$.subscribe(({ listAlias, fieldName }) => {
            this.editList(listAlias, fieldName);
        }));

        this.addList$ = this.moduleService.getAddList();
        this.subscriptions.push(this.addList$.subscribe(() => {
            this.addList();
        }));

        this.subscriptions.push(this.moduleService.getFieldsUpdated().subscribe(() => {
            this.getModuleContent();
        }));
    }

    ngOnDestroy(): void {
        this.ref.detach();
        this.subscriptions.forEach(sub => this.unsubscribe(sub));
    }

    private unsubscribe(subscription: Subscription) {
        if (subscription !== null) {
            subscription.unsubscribe();
        }
    }

    getModuleContent(): Promise<any> {
        return this.moduleService.loadModuleContent(this.tenantService.selectedTenant, this.moduleId, this.version);
    }

    selectStep(step: Step): void {
        this.moduleService.selectedStep = step;
    }

    startAddingNewField(fieldGroup: FieldGroup) {
        this.fieldService.startAddingCustomField(fieldGroup);
    }

    closeRightSidePanel($event) {
        this.rightside.close();
    }

    startEditingCustomField(fieldGroup: FieldGroup, field: Field) {
        this.fieldService.startEditingCustomField(fieldGroup, field);
    }



    setFieldModified(field: Field) {
        if (!field.IsCustomField) { field.Modified = true; }
        this.moduleService.updateField(
            field,
            this.tenantService.selectedTenant,
            this.moduleId,
            this.version,
            this.stepId
        ).subscribe(this.buildView);
    }

    deleteCustomField(field: Field): void {
        const references = findRuleReferences(this.moduleService.getModuleFields(), field);

        const dialogRef = this.dialog.open(PopupDialog);
        dialogRef.componentInstance.DialogTitle = 'Delete Field';
        dialogRef.componentInstance.DialogDescription = references.length > 0
            ? `Rules on these fields use this field: ${references.map(f => f.FieldId).join(', ')}. After you delete it, those rules stop working, and those fields always show. Are you sure?`
            : 'Are you sure?';
        dialogRef.componentInstance.DialogConfirmButtonText = 'Yes';
        dialogRef.componentInstance.DialogCancelButtonText = 'No';

        dialogRef.afterClosed().subscribe(result => {
            if (result && result.Result) {
                const f = new Field();
                f.FieldId = field.FieldId;
                f.FieldUniqueId = field.FieldUniqueId;
                f.FieldType = field.FieldType;
                f.GroupName = field.GroupName;
                f.IsCustomField = field.IsCustomField;

                this.applyPatch([{
                    Operation: 'Remove',
                    PropertyPaths: [[
                        ...this.moduleService.fieldsPath,
                        field.FieldId
                    ]]
                }]);

                this.moduleService.deleteField(
                    f,
                    this.tenantService.selectedTenant,
                    this.moduleId,
                    this.version,
                    this.stepId
                )
                    .then(() => this.buildView());
            }
        });
    }

    private applyPatch(patch: Patch[]) {
        return this.moduleService.applyPatch(this.tenantService.selectedTenant, this.moduleId, patch);
    }

    onDeleteCustomFieldClick(field: Field): void {
        this.linkedFieldsService.getIsFieldPartOfAnyLinkedFieldSets(this.tenantService.selectedTenant.TenantId, this.moduleId, this.version, field.FieldId)
            .subscribe(r => {
                if (r) {
                    this.toast.sendMessage('Unable to Delete',
                        'This field is part of one or more Linked Field Sets. You must remove the Linked Field Set before deleting this field',
                        ToastMessageType.error);
                }
                else {
                    this.formFlowService.IsFieldLinkedAnotherOfType(field.FieldId, this.tenantService.selectedTenant.TenantId, this.moduleId, this.version, 'calc')
                        .subscribe(result => {
                            if (result !== undefined && result) {
                                this.toast.sendMessage('Unable to update',
                                    'This field drives a calculation field. You must remove the link to the calculation before the field type can be deleted.',
                                    ToastMessageType.error);
                            }
                            else {
                                this.deleteCustomField(field);
                            }
                        });
                }
            });
    }

    buildView(): void {
        if (this.moduleId) {
            this.getModuleContent().then(() => {
                this.subHeaderText = this.moduleService.moduleContent.ModuleName;
                this.subSubHeaderText = '';

                this.moduleVersion = `version ${this.version}`;
            });
        }
    }

    prettifyLabel(lbl: string): string {
        let op: string;
        if (!lbl) {
            return '';
        }
        if (lbl.indexOf(':') > -1) {
            op = lbl.split(':')[1];
        } else {
            op = lbl;
        }

        return op.replace(/_/g, ' ');
    }

    editList(listAlias: string, fieldName: string) {
        const dialogRef = this.dialog.open(PopupDialog);
        dialogRef.componentInstance.DialogTitle = 'Manage List Items';
        dialogRef.componentInstance.DialogDescription = `Editing list items for ${fieldName}`;
        dialogRef.componentInstance.DialogListAlias = listAlias;
        dialogRef.componentInstance.DisplayListEditor = true;

        return dialogRef.afterClosed()
            .subscribe(res => {
                this.moduleService.notifyFieldUpdate();
                return res;
            });
    }

    addList() {
        const dialogRef = this.dialog.open(PopupDialog);
        dialogRef.componentInstance.DialogTitle = 'Add List';
        dialogRef.componentInstance.DialogDescription = 'Add list';
        dialogRef.componentInstance.DialogConfirmButtonText = 'Add list';
        dialogRef.componentInstance.DialogCancelButtonText = 'Cancel';
        dialogRef.componentInstance.DisplayListAdd = true;

        return dialogRef.afterClosed()
            .subscribe(res => {
                if (res.Result) {
                    this.tenantService.addList(res.Text)
                        .subscribe({
                            next: (): void => {
                                this.tenantService.getLists()
                                    .subscribe(r => {
                                        this.listAliases = r;
                                        this.moduleService.notifyFieldUpdate();
                                    });
                            },
                            error: (): void => {
                                this.toast.sendMessage('Unable to Add List',
                                    `Could not create list "${res.Text}". It may already exist.`,
                                    ToastMessageType.error);
                            }
                        });
                }
                return res;
            });
    }

    
    showStateTab(moduleId: string) {
        return moduleId ==
            ModuleUniqueIds.Events
            || moduleId == ModuleUniqueIds.Register
            || moduleId == ModuleUniqueIds.Risk
            || moduleId == ModuleUniqueIds.PermitToWork
            || moduleId == ModuleUniqueIds.TallyIndex;
    }
}
